import { ReactNode } from "react";

/**
 * SafeText: Wraps content to prevent layout breaks from long text
 *
 * Usage:
 * - Single line (truncate with ellipsis):
 *   <SafeText>{longText}</SafeText>
 *
 * - Multi-line (clamp to N lines):
 *   <SafeText lines={2}>{longText}</SafeText>
 *
 * - With tooltip on hover:
 *   <SafeText lines={1} tooltip={fullText}>{truncatedText}</SafeText>
 *
 * - No clamping, just safe wrapping:
 *   <SafeText wrap>{longText}</SafeText>
 */
interface SafeTextProps {
  children: ReactNode;
  /** Number of lines to clamp (1, 2, 3). Omit for no clamping. */
  lines?: 1 | 2 | 3;
  /** Allow text to wrap freely without clamping */
  wrap?: boolean;
  /** Tooltip to show on hover when text is clamped */
  tooltip?: string;
  /** Additional className */
  className?: string;
}

export function SafeText({
  children,
  lines,
  wrap,
  tooltip,
  className = "",
}: SafeTextProps) {
  const baseClasses = "break-words whitespace-normal";

  const clampClasses = {
    1: "line-clamp-1",
    2: "line-clamp-2",
    3: "line-clamp-3",
  };

  const clampClass = lines ? clampClasses[lines] : "";
  const finalClass = `${baseClasses} ${clampClass} ${className}`.trim();

  // If no tooltip, just return simple span
  if (!tooltip) {
    return <span className={finalClass}>{children}</span>;
  }

  // With tooltip
  return (
    <span className={finalClass} title={tooltip}>
      {children}
    </span>
  );
}
